import React from "react";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { ModalStackProvider } from "@/lib/modal-stack";

interface ModalEntry {
  type: string;
  props?: Record<string, any>;
}

const modalComponents: Record<string, React.ComponentType<any>> = {};

const { reducer, actions } = createSlice({
  name: "@libs/modals",
  initialState: {
    openedModals: [] as ModalEntry[],
    hiddenModals: [] as ModalEntry[],
  },
  reducers: {
    openModal(
      state,
      { payload }: PayloadAction<{ type: string; props?: Record<string, any> }>
    ) {
      state.openedModals.push({
        type: payload.type,
        props: payload.props,
      });
      return state;
    },
    closeModal(state, { payload }: PayloadAction<{ type: string }>) {
      state.openedModals = state.openedModals.filter(
        (modal) => modal.type !== payload.type
      );
      return state;
    },
    toggleModalVisibility(state, { payload }: PayloadAction<{ type: string }>) {
      const hasThisTypeOfModalOpenned = state.openedModals
        .map((m) => m.type)
        .includes(payload.type);
      if (!hasThisTypeOfModalOpenned) return state;

      if (state.hiddenModals.map((m) => m.type).includes(payload.type)) {
        state.hiddenModals = state.hiddenModals.filter(
          (modal) => modal.type !== payload.type
        );
      } else {
        state.hiddenModals.push({ type: payload.type });
      }
      return state;
    },
  },
});

export const { openModal, closeModal, toggleModalVisibility } = actions;

export function registerModal(
  name: string,
  Component: React.ComponentType<any>
) {
  modalComponents[name] = Component;

  return {
    open: (dispatch: any) => dispatch(openModal({ type: name })),
    close: (dispatch: any) => dispatch(closeModal({ type: name })),
  };
}

export function isModalOpened(state: any, modalType: string) {
  return state["@libs/modals"].openedModals.some(
    (modal: ModalEntry) => modal.type === modalType
  );
}

export function getReducer() {
  return {
    "@libs/modals": reducer,
  };
}

export function Modals() {
  const { openedModals, hiddenModals } = useSelector(
    (state: any) => state["@libs/modals"]
  );

  return (
    <>
      {openedModals.map((modal: any, index: number) => {
        const ModalComponent = modalComponents[modal.type];
        const isHidden = hiddenModals
          .map((m: any) => m.type)
          .includes(modal.type);
        if (ModalComponent) {
          return (
            <ModalStackProvider key={modal.type} parentDepth={index - 1}>
              <ModalComponent {...modal.props} isHidden={isHidden} />
            </ModalStackProvider>
          );
        }
        return null;
      })}
    </>
  );
}
